import PouchDB from 'pouchdb';
import PouchDBAuthentication from 'pouchdb-authentication';
import PouchDbMemory from 'pouchdb-adapter-memory';
import log from '@/logger';
import moment from 'moment';

import constants from '@/constants';

PouchDB.plugin(PouchDBAuthentication);
PouchDB.plugin(PouchDbMemory);
window.PouchDB = PouchDB;

const pouchWrapper = (db, pouchDb) => {
  if (pouchDb.name.match(/.*-log/)) {
    return pouchDb;
  }

  const origPost = pouchDb.post;
  const origPut = pouchDb.put;
  const origBulkDocs = pouchDb.bulkDocs;

  pouchDb.post = new Proxy(origPost, {
    apply: function (target, that, args) {
      let object = args[0];
      const reason = args[1];
      object = db.tagUpdate(object, reason);
      return Reflect.apply(target, that, [object]);
    },
  });

  pouchDb.put = new Proxy(origPut, {
    apply: function (target, that, args) {
      let object = args[0];
      const reason = args[1];
      object = db.tagUpdate(object, reason);
      return Reflect.apply(target, that, [object]);
    },
  });

  pouchDb.bulkDocs = new Proxy(origBulkDocs, {
    apply: function (target, that, args) {
      const docs = args[0];
      const reason = args[1];

      if (docs.length > 0 && docs[0].type) {
        const newDocs = [];
        for (let idx = 0; idx < docs.length; idx++) {
          newDocs[idx] = db.tagUpdate(docs[idx], reason);
        }
        return Reflect.apply(target, that, [newDocs]);
      }
      return Reflect.apply(target, that, args);
    },
  });

  return pouchDb;
};

export default {
  loggedInUsername: '',
  loggedInPassword: '',
  loadedDatabases: {},
  currentProjectDb: {},
  currentProjectLogDb: {},
  userDb: {},
  organisationDb: {},
  organisationLogDb: {},
  // favoriteKeywordDb: {},

  DB(dbsection, username) {
    return {
      baseUrl: constants.db.serverUrl,
      db: this.getDbName(constants.db.name, dbsection),
      localDb: this.getDbName(constants.db.name, dbsection, username),
      options: {
        adapter: process.env.NODE_ENV !== 'production' && document.boonTesting ? 'memory' : '',
      },
    };
  },

  async init(dbsection, username) {
    const dbOptions = this.DB(dbsection, username);
    this.loadedDatabases[dbsection] = pouchWrapper(this, new PouchDB(dbOptions.localDb, dbOptions.options));
    log.info(`Initiating db ${this.loadedDatabases[dbsection].name}`, [this.loadedDatabases[dbsection].adapter]);
    const info = await this.loadedDatabases[dbsection].info();
    console.log(
      `%cDb's got %c${info.doc_count}%c documents`,
      'background-color: #88aaff; border-radius: 3px 0 0 3px;',
      'background-color: #88ffaa; border-radius: 0px;',
      'background-color: #88aaff; border-radius: 0 3px 3px 0;'
    );
    log.info(`Initiating db ${info.db_name}`, { info });

    return this.loadedDatabases[dbsection];
  },

  initTests(test) {
    const g = Math.floor(100000 * Math.random());

    console.log();
    console.log();
    console.log('Init db for test ', test, g);
    console.log();

    document.boonTesting = true;
    let dbOptions = this.DB('unitproject');
    this.currentProjectDb = new PouchDB(dbOptions.localDb, dbOptions.options);
    this.initViews(this.currentProjectDb);
    this.currentProjectLogDb = this.currentProjectDb;
    this.loadedDatabases['unitproject'] = this.currentProjectDb;

    dbOptions = this.DB(`unitorganisation${g}`);
    this.organisationDb = pouchWrapper(this, new PouchDB(dbOptions.localDb, dbOptions.options));
    this.initViews(this.organisationDb);
    this.organisationLogDb = this.organisationDb;

    this.userDb = this.currentProjectDb;
  },

  reInitUserdbForTests() {
    this.userDb = this.currentProjectDb;
  },

  async initViews(db) {
    const promises = [
      this.recreateIndex(
        db,
        'parts',
        `function (doc) {
          if (doc.type === 'Part' && !doc.archived) {
            emit([doc.projectId, doc._id]);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'stages',
        `function (doc) {
          if (doc.type === 'Stage' && !doc.archived) {
            emit([doc.projectId, doc._id]);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'projectList',
        `function (doc) {
          if (doc.type === 'Project' && !doc.archived) {
            emit(doc._id, {
              title: doc.title,
              projectNumber: doc.projectNumber,
              archived: false,
              projectIcon: doc.projectIcon,
              lockedForEditingBy: doc.lockedForEditingBy,
              projectStartdate: doc.projectStartdate,
              projectEnddate: doc.projectEnddate,
              buildingType: doc.buildingType,
              projectType: doc.projectType,
              address: doc.address,
              projectManagers: doc.projectManagers,
              projectSubcontractors: doc.projectSubcontractors
            });
          }
        }`
      ),
      this.recreateIndex(
        db,
        'companyList',
        `function (doc) {
          if (doc.type === 'Company' && !doc.archived) {
            emit(doc._id);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'companyFiles',
        `function (doc) {
          if (doc.type === 'CompanyFiles' && !doc.archived) {
            emit(doc.companyId);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'diaryEntries',
        `function (doc) {
          if (doc.type === 'DiaryEntry' && !doc.archived) {
            emit([doc.projectId, doc._id]);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'partComments',
        `function (doc) {
          if (doc.type === 'PartComment' && !doc.archived) {
            emit([doc.projectId, doc.partId, doc.commentdate, doc._id]);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'todoItems',
        `function (doc) {
          if (doc.type === 'TodoItem' && !doc.archived) {
            emit([doc.projectId, doc.partId]);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'todoTemplates',
        `function (doc) {
          if (doc.type === 'TodoTemplate' && !doc.archived) {
            emit([doc.projectId, doc.title, doc._id]);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'inspections',
        `function (doc) {
          if (doc.type === 'Inspection' && !doc.archived) {
            emit([doc.projectId]);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'inspectionTemplates',
        `function (doc) {
          if (doc.type === 'InspectionTemplate' && !doc.archived) {
            emit([doc.projectId, doc._id]);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'personList',
        `function (doc) {
          if (doc.type === 'Person' && !doc.archived) {
            emit(doc._id);
          }
        }`
      ),
      this.recreateIndex(
        db,
        'partCommentsForPerson',
        `function (doc) {
          if (doc.type === 'PartComment' && !doc.archived && doc.mentions) {
            for(var personId in doc.mentions) {
              if (doc.mentions.hasOwnProperty(personId)) {
                emit([personId, doc.commentdate, doc._id]);
              }
            }
          }
        }`
      ),
      this.recreateIndex(
        db,
        'keyword',
        `function (doc) {
          if (doc.type === 'Keyword' && !doc.archived) {
            emit(doc._id);
          }
        }`
      ),
    ];

    return Promise.all(promises);
  },

  async initLogViews(db) {
    const promises = [
      this.recreateIndex(
        db,
        'projectLog',
        `function (doc) {
          emit([doc.projectId, doc.timestamp]);
        }`
      ),
    ];

    return Promise.all(promises);
  },

  async getLogDb(projectId) {
    const dbProps = this.DB(projectId);
    const url = new URL(`${dbProps.db}-log`, dbProps.baseUrl);
    url.username = this.loggedInUsername;
    url.password = this.loggedInPassword;
    const logDb = new PouchDB(url.toString());
    await this.recreateIndex(
      logDb,
      'projectLog',
      `function (doc) {
        if (doc.type === 'Log') {
          emit([doc.projectId, doc.timestamp]);
        }
      }`
    );
    return logDb;
  },

  async getDoc(db, id) {
    try {
      return await db.get(id);
    } catch (error) {
      // Just swallowing exceptions
      return null;
    }
  },

  async recreateIndex(db, indexName, map) {
    const id = `_design/${indexName}`;
    const doc = await this.getDoc(db, id);
    if (doc && doc.views && doc.views[indexName] && doc.views[indexName].map === map) {
      return doc;
    }

    log.debug(`Creating index ${id}`);
    const index = {
      _id: id,
      views: {
        [indexName]: { map },
      },
    };

    if (doc && doc._rev) {
      index._rev = doc._rev;
    }

    return db.put(index);
  },

  getBasicAuthHeaders() {
    if (!this.loggedInUsername || !this.loggedInPassword) {
      return {};
    }

    return {
      // Encoding of token is taken from `pouchdb-authentication/lib/index.browser.es.js`
      Authorization: `Basic ${btoa(unescape(encodeURIComponent(`${this.loggedInUsername}:${this.loggedInPassword}`)))}`,
    };
  },

  tagUpdate(doc, reason) {
    if (!doc || !doc.type) {
      return doc;
    }

    return {
      ...doc,
      lastUpdate: {
        user: this.loggedInUsername,
        reason: reason || '',
        timestamp: moment().toISOString(),
      },
    };
  },

  getDbName(basename, dbsection, username) {
    const name = [basename, dbsection, username].filter(Boolean).join('-');

    return name.replace(/[^a-zA-Z0-9\\-]/g, '').toLowerCase();
  },
};



// WEBPACK FOOTER //
// ./src/store/db.js